/**
 * FormRenderer Component
 * Main component for rendering dynamic CRF forms
 */

import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTemplate, FormState, DataEntry } from '../../types/form';
import { FormSection } from './FormSection';
import { Send, FileSignature } from 'lucide-react';

interface FormRendererProps {
  template: FormTemplate;
  data?: DataEntry;
  onSave: (data: FormState) => void;
  onSign?: (data: FormState, meaning: string) => void;
  disabled?: boolean;
  mode?: 'edit' | 'view' | 'review';
  visitName?: string;
  visitId?: string;
  subjectId?: string;
  dataEntryId?: string;
}

export const FormRenderer: React.FC<FormRendererProps> = ({
  template,
  data,
  onSave,
  onSign,
  disabled = false,
  mode = 'edit',
  visitName,
  visitId,
  subjectId,
  dataEntryId,
}) => {
  // Guard against missing template or sections
  if (!template) {
    return (
      <div className="bg-red-50 rounded-lg p-6 text-center">
        <p className="text-red-700">No form template provided</p>
      </div>
    );
  }

  if (!template.sections || template.sections.length === 0) {
    return (
      <div className="bg-yellow-50 rounded-lg p-6 text-center">
        <p className="text-yellow-700">Form template has no sections defined</p>
        <p className="text-sm text-yellow-600 mt-2">Form ID: {template.formId}</p>
      </div>
    );
  }
  // Convert DataEntry data array to form state object
  const getDefaultValues = (): FormState => {
    const values: FormState = {};
    if (data?.data) {
      data.data.forEach((field) => {
        const key = field.repeatIndex !== null && field.repeatIndex !== undefined
          ? `${field.fieldId}_${field.repeatIndex}`
          : field.fieldId;
        values[key] = field.value;
      });
    }
    return values;
  };

  const methods = useForm<FormState>({
    defaultValues: getDefaultValues(),
    mode: 'onBlur',
  });

  const { handleSubmit, reset, getValues, formState: { isDirty, isSubmitting } } = methods;

  // Reset form when data changes
  useEffect(() => {
    reset(getDefaultValues());
  }, [data, reset]);

  // Sort sections by position, filter by visit if showForVisits is set
  const sortedSections = [...(template.sections || [])]
    .filter((s: any) => !s.showForVisits?.length || !visitId || s.showForVisits.includes(visitId))
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const isLocked = data?.isLocked || false;
  const isViewMode = mode === 'view' || isLocked;

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => e.preventDefault()} className="max-w-4xl mx-auto">
        {/* Form Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">
              {template.formName}
              {visitName && (
                <span className="text-base font-normal text-gray-400"> — {visitName}</span>
              )}
            </h1>
            {data && (data as any).completionStatus?.requiredFields > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  {(data as any).completionStatus.completedRequired}/{(data as any).completionStatus.requiredFields}
                </span>
                <div className="w-24 bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      (data as any).completionStatus.completedRequired === (data as any).completionStatus.requiredFields
                        ? 'bg-green-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${((data as any).completionStatus.completedRequired / (data as any).completionStatus.requiredFields) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Sections */}
        {sortedSections.map((section) => (
          <FormSection
            key={section.sectionId}
            section={section}
            disabled={isViewMode || disabled}
            subjectId={subjectId}
            dataEntryId={dataEntryId}
          />
        ))}

        {/* Signature Section */}
        {template.signatureRequired && !isViewMode && (
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-6 mb-6">
            <div className="flex items-start gap-3">
              <FileSignature className="text-amber-600 mt-1" size={24} />
              <div>
                <h3 className="font-semibold text-amber-800">Electronic Signature Required</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {template.signatureMeaning || 'I confirm that the data in this form is accurate and complete.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!isViewMode && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {isDirty && <span className="text-amber-600">You have unsaved changes</span>}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onSave(getValues())}
                  disabled={isSubmitting || disabled}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <Send size={16} />
                  Send
                </button>

                {onSign && template.signatureRequired && (
                  <button
                    type="button"
                    onClick={() => {
                      handleSubmit((formData) => onSign(formData, template.signatureMeaning || 'Confirmed'))();
                    }}
                    disabled={isSubmitting || disabled}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    <FileSignature size={16} />
                    Sign
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Query Count */}
        {data && data.openQueryCount > 0 && (
          <div className="mt-6 bg-red-50 rounded-lg border border-red-200 p-4">
            <p className="text-red-700 font-medium">
              {data.openQueryCount} open {data.openQueryCount === 1 ? 'query' : 'queries'} on this record
            </p>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default FormRenderer;
